import { preloadRoute } from "../route-preload.js";
import { useState, useEffect, useMemo, useRef, Suspense } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { hasAtLeast, isPortalFeatureEnabled, ROLES, type Role } from "@wwrp/shared";
import { useAuth } from "../contexts/AuthContext.js";
import { getAccessRole } from "../api/auth.js";
import { IconSearch } from "@tabler/icons-react";
import classes from "./NavbarSimple.module.css";
import headerClasses from "./Header.module.css";
import { CurrentUser } from "./CurrentUser.js";
import { NotificationsButton } from "./NotificationsButton.js";
import { ThemeButton, useThemeCycle, THEME_ICON, THEME_LABEL } from "./ThemeButton.js";
import { AppSpotlight } from "./AppSpotlight.js";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Modal,
  Select,
  Text,
  Title,
  Skeleton,
  Stack,
  Tooltip,
} from "@mantine/core";
import { spotlight } from "@mantine/spotlight";
import wwrpLogo from "../static/wwrp_logo2.png";
import {
  getNavPageForPathname,
  getPortalFeatureKey,
  NAV_CATEGORIES,
  NAV_PAGES,
  type NavCategoryId,
} from "../nav.js";
import { PortalFeatureGuard } from "./PortalFeatureGuard.js";
import { ROLE_LABEL } from "./RoleBadge.js";
import { usePortalFeatures } from "../hooks/usePortalFeatures.js";

export function Layout() {
  const { user, setViewAsRole, clearViewAsRole } = useAuth();
  const portalFeatures = usePortalFeatures();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobileNav, setIsMobileNav] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
  );
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const roleBannerRef = useRef<HTMLDivElement>(null);
  const [roleBannerHeight, setRoleBannerHeight] = useState(0);
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<NavCategoryId | null>(null);

  useEffect(() => {
    setMenuOpen(false);
    setSelectedCategory(null);
  }, [location.pathname]);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 767px)");
    const onChange = (event: MediaQueryListEvent) => {
      setIsMobileNav(event.matches);
      if (!event.matches) setMenuOpen(false);
    };
    if (query.addEventListener) {
      query.addEventListener("change", onChange);
      return () => query.removeEventListener("change", onChange);
    }
    // Older mobile WebViews only implement the legacy MediaQueryList listener API.
    query.addListener(onChange);
    return () => query.removeListener(onChange);
  }, []);

  useEffect(() => {
    const banner = roleBannerRef.current;
    if (!banner) {
      setRoleBannerHeight(0);
      return;
    }
    const updateHeight = () =>
      setRoleBannerHeight(Math.ceil(banner.getBoundingClientRect().height));
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(banner);
    return () => observer.disconnect();
  }, [user?.viewingAsRole]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!isMobileNav || !menuOpen) return;

    const focusable = () =>
      Array.from(
        navRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
    const items = focusable();
    items[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setMenuOpen(false);
        menuButtonRef.current?.focus();
        return;
      }
      if (event.key !== "Tab") return;

      const currentItems = focusable();
      if (currentItems.length === 0) return;
      const first = currentItems[0];
      const last = currentItems[currentItems.length - 1];
      if (
        event.shiftKey &&
        (document.activeElement === first || !navRef.current?.contains(document.activeElement))
      ) {
        event.preventDefault();
        last?.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || !navRef.current?.contains(document.activeElement))
      ) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isMobileNav, menuOpen]);

  const visibleItems = useMemo(
    () =>
      NAV_PAGES.filter((item) => {
        if (item.navigationHidden) return false;
        const featureKey = getPortalFeatureKey(item);
        if (featureKey && !isPortalFeatureEnabled(portalFeatures.data?.features, featureKey))
          return false;
        if (!user) return !item.minRole && !item.permissionKey && !item.sysadminOnly;
        if (item.sysadminOnly) return user.isSysadmin && !user.viewingAsRole;
        if (item.permissionKey) return user.permissions?.[item.permissionKey] ?? false;
        const accessRole = getAccessRole(user);
        return !item.minRole || (accessRole !== null && hasAtLeast(accessRole, item.minRole));
      }),
    [user, portalFeatures.data?.features],
  );

  const { current: themeScheme, cycle: cycleTheme } = useThemeCycle();
  const categories = NAV_CATEGORIES.filter((category) =>
    visibleItems.some((item) => item.category === category.id),
  );
  const activePage = getNavPageForPathname(location.pathname);
  const activeFeatureKey = activePage ? getPortalFeatureKey(activePage) : null;
  const routeCategory = activePage?.category ?? "home";
  const activeCategory =
    categories.find((category) => category.id === selectedCategory) ??
    categories.find((category) => category.id === routeCategory) ??
    categories[0];
  const categoryItems = visibleItems.filter((item) => item.category === activeCategory?.id);
  const sections =
    activeCategory?.sections.filter((section) =>
      categoryItems.some((item) => item.section === section),
    ) ?? [];
  const ThemeIcon = THEME_ICON[themeScheme];
  const canViewAs = !!user && (user.isSysadmin || hasAtLeast(user.trueRole, "head_administration"));
  const isViewingAs = !!user?.viewingAsRole;

  useEffect(() => {
    setSelectedRole(user?.viewingAsRole ?? user?.role ?? null);
  }, [user?.viewingAsRole, user?.role]);

  return (
    <div className="app-shell">
      {isViewingAs && (
        <Box
          ref={roleBannerRef}
          className="role-view-banner"
          style={{
            background: "color-mix(in srgb, var(--warning) 16%, var(--bg-elev))",
            borderBottom: "1px solid color-mix(in srgb, var(--warning) 35%, var(--border))",
            padding: "8px 14px",
          }}
        >
          <Group justify="space-between" wrap="wrap" gap="xs">
            <Text size="sm" fw={600}>
              Role-view mode active: viewing Portal as{" "}
              {ROLE_LABEL[user!.viewingAsRole as Role] ?? user!.viewingAsRole}
            </Text>
            <Group gap="xs">
              {canViewAs && (
                <Button size="xs" variant="light" onClick={() => setViewAsOpen(true)}>
                  Change role
                </Button>
              )}
              <Button
                size="xs"
                color="orange"
                variant="filled"
                onClick={() => void clearViewAsRole()}
              >
                Return to normal
              </Button>
            </Group>
          </Group>
        </Box>
      )}

      <header className={`app-header ${headerClasses.root}`}>
        <button
          ref={menuButtonRef}
          className={`hamburger${menuOpen ? " hamburger--open" : ""}`}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label={menuOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={menuOpen}
          aria-controls="sidebar-nav"
          type="button"
        >
          <span className="hamburger__line" />
          <span className="hamburger__line" />
          <span className="hamburger__line" />
        </button>

        <div className={headerClasses.left}>
          <Link to="/" className={headerClasses.logo} aria-label="WWRP Portal home">
            <img src={wwrpLogo} alt="WWRP Portal" width={921} height={236} />
          </Link>
        </div>
        <div className={headerClasses.rightContent}>
          {canViewAs && (
            <Tooltip label="View Portal as another role" position="bottom" visibleFrom="sm">
              <Button
                size="xs"
                variant={isViewingAs ? "filled" : "light"}
                color={isViewingAs ? "orange" : "brand"}
                onClick={() => setViewAsOpen(true)}
                visibleFrom="sm"
              >
                View as role
              </Button>
            </Tooltip>
          )}
          <Tooltip label="Search (⌘K)" position="bottom" visibleFrom="sm">
            <ActionIcon
              variant="transparent"
              c="inherit"
              size={42}
              onClick={spotlight.open}
              aria-label="Open command palette"
              visibleFrom="sm"
            >
              <IconSearch stroke={1.5} />
            </ActionIcon>
          </Tooltip>
          <Box visibleFrom="sm">
            <ThemeButton />
          </Box>
          <Box onClickCapture={() => isMobileNav && setMenuOpen(false)}>
            <NotificationsButton />
          </Box>
          <Box onClickCapture={() => isMobileNav && setMenuOpen(false)}>
            <CurrentUser />
          </Box>
        </div>
      </header>

      <div className="app-body">
        <div
          className={`sidebar-backdrop${menuOpen ? " sidebar-backdrop--open" : ""}`}
          style={{
            top: isMobileNav
              ? `calc(${roleBannerHeight}px + 60px + env(safe-area-inset-top))`
              : undefined,
          }}
          onClick={() => {
            setMenuOpen(false);
            menuButtonRef.current?.focus();
          }}
          aria-hidden="true"
        />

        <nav
          ref={navRef}
          id="sidebar-nav"
          className={`sidebar${menuOpen ? " sidebar--open" : ""} ${classes.navbar}`}
          style={{
            top: isMobileNav
              ? `calc(${roleBannerHeight}px + 60px + env(safe-area-inset-top))`
              : undefined,
          }}
          aria-label="Main navigation"
          aria-hidden={isMobileNav && !menuOpen}
          inert={isMobileNav && !menuOpen}
        >
          <div className={classes.categoryRail} aria-label="Categories">
            {categories.map((category) => (
              <button
                type="button"
                key={category.id}
                className={classes.categoryButton}
                data-active={category.id === activeCategory?.id || undefined}
                aria-pressed={category.id === activeCategory?.id}
                aria-controls="category-sections"
                onClick={() => setSelectedCategory(category.id)}
              >
                <category.icon size={22} stroke={1.6} aria-hidden="true" />
                <span>{category.label}</span>
              </button>
            ))}
          </div>
          <div
            className={classes.sectionPanel}
            id="category-sections"
            aria-label={`${activeCategory?.label ?? "Portal"} sections`}
          >
            <Text className={classes.categoryTitle} fw={600}>
              {activeCategory?.label}
            </Text>
            <div className={classes.navbarMain}>
              {sections.map((section) => (
                <section key={section} className={classes.sectionGroup}>
                  <h2 className={classes.sectionHeading}>{section}</h2>
                  <div className={classes.sectionLinks}>
                    {categoryItems
                      .filter((item) => (item.section ?? "Pages") === section)
                      .map((item) => {
                        const isActive = item.end
                          ? location.pathname === item.to
                          : location.pathname.startsWith(item.to);
                        return (
                          <Link
                            key={item.id}
                            to={item.to}
                            onClick={() => {
                              if (isMobileNav) {
                                setMenuOpen(false);
                                menuButtonRef.current?.focus();
                              }
                            }}
                            onMouseEnter={() => preloadRoute(item.to)}
                            onFocus={() => preloadRoute(item.to)}
                            className={classes.link}
                            data-active={isActive || undefined}
                            aria-current={isActive ? "page" : undefined}
                          >
                            <span>{item.label}</span>
                          </Link>
                        );
                      })}
                  </div>
                </section>
              ))}
            </div>
            <Box hiddenFrom="sm" className={classes.footer}>
              <button
                type="button"
                className={classes.link}
                onClick={() => {
                  setMenuOpen(false);
                  spotlight.open();
                }}
              >
                <IconSearch className={classes.linkIcon} stroke={1.5} />
                <span>Search</span>
              </button>
              <button type="button" className={classes.link} onClick={cycleTheme}>
                <ThemeIcon className={classes.linkIcon} stroke={1.5} />
                <span>{THEME_LABEL[themeScheme]}</span>
              </button>
              {canViewAs && (
                <button
                  type="button"
                  className={classes.link}
                  onClick={() => {
                    setMenuOpen(false);
                    setViewAsOpen(true);
                  }}
                >
                  <span>{isViewingAs ? "Change role" : "View as role"}</span>
                </button>
              )}
            </Box>
          </div>
        </nav>

        <main className="main-content">
          <Suspense
            key={location.pathname}
            fallback={
              <div className="page" role="status" aria-label="Loading page">
                <Title order={1} className="page-title" mb="md">
                  {visibleItems.find((item) => item.to === location.pathname)?.label ??
                    "Loading page"}
                </Title>
                <Stack gap="sm">
                  <Skeleton height={48} />
                  <Skeleton height={160} />
                </Stack>
              </div>
            }
          >
            {activeFeatureKey ? (
              <PortalFeatureGuard feature={activeFeatureKey}>
                <Outlet />
              </PortalFeatureGuard>
            ) : (
              <Outlet />
            )}
          </Suspense>
        </main>
      </div>

      <AppSpotlight />

      <Modal opened={viewAsOpen} onClose={() => setViewAsOpen(false)} title="View Portal As Role">
        <Text size="sm" c="dimmed" mb="sm">
          This changes your effective permissions and visibility across the Portal until you return
          to normal.
        </Text>
        <Select
          label="Role"
          data={ROLES.map((r) => ({ value: r, label: ROLE_LABEL[r] ?? r }))}
          value={selectedRole}
          onChange={(v) => setSelectedRole((v as Role | null) ?? null)}
        />
        <Group justify="space-between" mt="md">
          <Button variant="subtle" onClick={() => setViewAsOpen(false)}>
            Cancel
          </Button>
          <Group>
            <Button
              variant="light"
              color="gray"
              onClick={() => {
                void clearViewAsRole();
                setViewAsOpen(false);
              }}
            >
              Return to normal
            </Button>
            <Button
              onClick={() => {
                if (!selectedRole) return;
                void setViewAsRole(selectedRole);
                setViewAsOpen(false);
              }}
            >
              Apply
            </Button>
          </Group>
        </Group>
      </Modal>
    </div>
  );
}
